import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { OtpModalComponent } from '@teem/zatca-phase-two-module/modals/otp-modal/otp-modal.component';

import { ZatcaService } from '@teem/services/zatca.service';
import { LoaderService } from '@teem/services/loader.service';
import { messages as i18n } from '@teem/constants/messages';

@Component({
	selector: 'app-generate-certificate',
	templateUrl: './generate-certificate.component.html',
	styleUrls: ['./generate-certificate.component.scss']
})
export class GenerateCertificateComponent implements OnInit {
	isSubmitted: boolean = false;
	certificateBuilderForm: FormGroup;
	certificate: any;
	isViewReady = false;
	certificateFrom = true;

	constructor(
		private fb: FormBuilder, 
		private modalService: NgbModal,
		private zatcaService: ZatcaService,
		private loaderService: LoaderService
	) { }

	ngOnInit(): void {
		this.getCertificate();
	}

	get f() {
		return this.certificateBuilderForm?.controls;
	}

	initCertificateBuilderForm() {
		this.certificateBuilderForm = this.fb.group({
			organization_id: [this.certificate?.organization_id, [Validators.required, Validators.pattern(/^3\d{13}3$/)]],
			// solution_name: [this.certificate?.solution_name, [Validators.required]],
			// model: [this.certificate?.model, [Validators.required]],
			// serial_number: [this.certificate?.serial_number, [Validators.required]],
			common_name: [this.certificate?.common_name, [Validators.required]],
			organization_name: [this.certificate?.organization_name, Validators.required],
			organizational_unit_name: [this.certificate?.organizational_unit_name, Validators.required],
			// address: [this.certificate?.address, Validators.required],
			building_number: [this.certificate?.building_number, Validators.required],
			street: [this.certificate?.street, Validators.required],
			city: [this.certificate?.city, Validators.required],
			business_category: [this.certificate?.business_category, Validators.required],
			country_name: ['SA', Validators.required],
			invoice_type: [1100, Validators.required],
			production: [false, Validators.required],
		});
	}

	getCertificate(): void {
		this.zatcaService.getCertificate().subscribe(
			(data: any) => {
				this.certificate = data?.certificate;
				// if (!this.certificate) {
					this.initCertificateBuilderForm();
				// }
				this.isViewReady = true;
			},
			(error) => {
				this.initCertificateBuilderForm();
				this.isViewReady = true;
			}
		);
	}

	onSubmit() {
		this.isSubmitted = true;
		if (this.certificateBuilderForm.valid) {
			this.loaderService.startLoading();
			let fd = this.certificateBuilderForm.value;
			this.zatcaService.generateCertificate(fd).subscribe(
				(res: any) => {
					this.certificate = res?.certificate;
					this.isSubmitted = false;
					this.certificateFrom = true;
					this.loaderService.stopLoading();
					Swal.fire(i18n.done, i18n.zatca.certificate_generated_successfully, 'success');
				},
				(err) => {
					this.loaderService.stopLoading();
					// Set server side error
					if (err.status === 422) {
						if (err.error && err.error.errors && Object.keys(err.error.errors).length) {
							let validationErrors = err.error.errors;
							Object.keys(validationErrors).forEach((prop) => {
								this.certificateBuilderForm.controls[prop]?.setErrors({
									serverError: validationErrors[prop]
								})
							})
						}
					}
					Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
				} 
			);
			this.isSubmitted = false;
		}
	}

	updateStatus(uuid, event: any) {
		let checked = event.target.checked;
		this.certificate.is_enable = !checked;
		Swal.fire({
			title: i18n.are_you_sure,
			text: i18n.you_want_to_update_status,
			icon: 'info',
			showCloseButton: true,
			showCancelButton: true,
			focusConfirm: false,
			confirmButtonText: i18n.yes,
			cancelButtonText: i18n.no_cancel_it
		}).then(result => {
			if (result.value) {
				let fd: any = {};
				fd.uuid = uuid;
				this.loaderService.startLoading();
				this.zatcaService.updateStatus(fd).subscribe((data: any) => {
					this.loaderService.stopLoading();
					event.target.checked = checked;
					this.certificate.is_enable = !checked;
					setTimeout(function () {
						Swal.fire(i18n.done, i18n.status_updated_successfully, 'success');
					}, 0);	
				}, err => {
					this.loaderService.stopLoading();
					event.target.checked = !checked;
					this.certificate.is_enable = checked;
					Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
				})
			} else {
				event.target.checked = !checked;
				this.certificate.is_enable = checked;
				return false;
			}
		});
	}

	generateComplianceCertificate(type: 'compliance' | 'renew') {
		const modalRef = this.modalService.open(OtpModalComponent, {
			size: 'md',
			centered: true,
			backdrop: 'static',
			keyboard: false
		});

		// ✅ PASS INPUT TO MODAL
		modalRef.componentInstance.type = type;

		modalRef.result.then(result => {
			this.certificate = result;
		}, fallbackResult => {
			// 
		})
	}

	generateOnboardingCSIDCertificate(): void {
		this.loaderService.startLoading();
		this.zatcaService.generatepProductionCertificate().subscribe(
			(res: any) => {
				this.loaderService.stopLoading();
				this.certificate = res?.certificate;
				Swal.fire(i18n.done, i18n.zatca.certificate_generated_successfully, 'success');
			},
			(err) => {
				this.loaderService.stopLoading();
				Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
			} 
		);
	}

	revokeCertificate(uuid) {
		Swal.fire({
			title: i18n.are_you_sure,
			text: "You want to revoke certificate",
			icon: 'info',
			showCloseButton: true,
			showCancelButton: true,
			focusConfirm: false,
			confirmButtonText: i18n.yes,
			cancelButtonText: i18n.no_cancel_it
		}).then(result => {
			if (result.value) {
				let fd: any = {};
				fd.uuid = uuid;
				fd.status = 'revoked';
				this.loaderService.startLoading();
				this.zatcaService.updateStatus(fd).subscribe((data: any) => {
					this.loaderService.stopLoading();
					this.certificate = data.certificate;
					setTimeout(function () {
						Swal.fire(i18n.done, 'Certificate revoked successfully', 'success');
					}, 0);	
				}, err => {
					this.loaderService.stopLoading();
					Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
				})
			} else {
				// 
			}
		});
	}

	generateCertificate() {
		this.certificateFrom = false;
		this.initCertificateBuilderForm();
	}

	back() {
		this.certificateFrom = true;
	}

}
